import React, { useMemo, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "./firebase";

/* ===== MikDok Tokens (white UI) ===== */
const MD = {
  PRIMARY: "#00A8FF",
  BORDER: "#E5E7EB",
  TEXT: "#0F172A",
  MUTED: "#64748B",
  ERROR_BG: "#FEE2E2",
  ERROR_TEXT: "#7F1D1D",
  ERROR_BORDER: "#FECACA",
  OK_BG: "#ECFDF5",
  OK_TEXT: "#065F46",
  OK_BORDER: "#BBF7D0",
};

const dict = {
  en: {
    brandTag: "Patient quick start",
    title: "Find your perfect healthcare provider",
    subtitle: "Quick setup to get you going. You can add details later.",
    fullName: "Full name",
    phone: "Phone number",
    lang: "Preferred language",
    privacy: "I have read and accept the Privacy Policy.",
    privacyShort: "Privacy Policy",
    save: "Start using MikDok",
    logout: "Log out",
    phoneHint: "Used for confirmations and reminders.",
    phoneInvalid: "Please enter a valid phone number.",
    requiredError:
      "Please enter your name, a valid phone number, and accept the Privacy Policy.",
    saved: "Profile saved.",
  },
  sq: {
    brandTag: "Nisje e shpejtë për pacienta",
    title: "Gjeni ofruesin tuaj ideal të kujdesit shëndetësor",
    subtitle:
      "Vendos pak të dhëna për të filluar. Mund t’i shtoni detajet më vonë.",
    fullName: "Emri i plotë",
    phone: "Numri i telefonit",
    lang: "Gjuha e preferuar",
    privacy: "Kam lexuar dhe pranoj Politikën e Privatësisë.",
    privacyShort: "Politika e Privatësisë",
    save: "Fillo me MikDok",
    logout: "Dil",
    phoneHint: "Përdoret për konfirmime dhe njoftime.",
    phoneInvalid: "Ju lutem vendosni një numër telefoni të vlefshëm.",
    requiredError:
      "Ju lutem vendosni emrin, një numër telefoni të vlefshëm dhe pranoni Politikën e Privatësisë.",
    saved: "Profili u ruajt.",
  },
};

export default function PatientProfileSetup() {
  const navigate = useNavigate();

  // UI language (labels)
  const [uiLang, setUiLang] = useState("en");
  const t = useMemo(() => dict[uiLang], [uiLang]);

  // Minimal profile
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("en"); // saved in profile
  const [privacy, setPrivacy] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const phoneOk = (v) => (v || "").replace(/\D/g, "").length >= 7;
  const canSubmit =
    fullName.trim().length > 1 && phoneOk(phone) && privacy && !loading;

  const logout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch {
      setErr("Could not log out. Please try again.");
    }
  };

  const save = async () => {
    setErr("");
    setOk("");
    if (!canSubmit) {
      setErr(t.requiredError);
      return;
    }
    try {
      setLoading(true);
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setErr("No authenticated user. Please sign in and try again.");
        setLoading(false);
        return;
      }
      await setDoc(
        doc(db, "users", uid),
        {
          fullName: fullName.trim(),
          phone: phone.trim(),
          preferredLanguage,
          privacyPolicyAcknowledged: privacy,
          profileComplete: true,
          profileCompletedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
        },
        { merge: true }
      );
      setOk(t.saved);
      setTimeout(() => navigate("/dashboard-patient"), 600);
    } catch (e) {
      setErr(e?.message || "Failed to save profile.");
    } finally {
      setLoading(false);
    }
  };

  /* ===== Styles: white, brand-blue accents, big tap targets ===== */
  const S = {
    page: {
      minHeight: "100vh",
      background: "#FFFFFF",
      display: "grid",
      placeItems: "center",
      padding: 20,
    },
    card: {
      width: "100%",
      maxWidth: 760,
      background: "#FFFFFF",
      border: `1px solid ${MD.BORDER}`,
      borderRadius: 16,
      boxShadow: "0 20px 50px rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      borderBottom: `1px solid ${MD.BORDER}`,
    },
    brandWrap: { display: "flex", alignItems: "center", gap: 12 },
    logo: { width: 28, height: 28, borderRadius: 6, objectFit: "contain" },
    wordmark: { fontWeight: 800, color: MD.TEXT, letterSpacing: "-0.02em" },
    tag: { fontSize: 12, color: MD.MUTED, marginTop: 2 },
    brandCol: { display: "flex", flexDirection: "column" },
    rightHead: { display: "flex", alignItems: "center", gap: 8 },
    langSel: {
      border: `1px solid ${MD.BORDER}`,
      borderRadius: 10,
      padding: "8px 10px",
      fontSize: 14,
      color: MD.TEXT,
      background: "#FFFFFF",
      cursor: "pointer",
    },
    logout: {
      border: `1px solid ${MD.BORDER}`,
      background: "#FFFFFF",
      color: MD.TEXT,
      borderRadius: 10,
      padding: "8px 12px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },

    main: { padding: 20 },
    title: {
      margin: "2px 0 4px",
      fontSize: 22,
      fontWeight: 750,
      color: MD.TEXT,
      letterSpacing: "-0.02em",
    },
    subtitle: { margin: "0 0 14px", color: MD.MUTED, fontSize: 14 },

    form: { width: "100%", maxWidth: 520 },
    label: {
      display: "block",
      margin: "14px 0 6px",
      color: MD.TEXT,
      fontWeight: 600,
      fontSize: 14,
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: 12,
      border: `1.5px solid ${MD.BORDER}`,
      background: "#FFFFFF",
      fontSize: 16,
      color: MD.TEXT,
      outline: "none",
      transition: "box-shadow .15s ease, border-color .15s ease",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: 12,
      border: `1.5px solid ${MD.BORDER}`,
      background: "#FFFFFF",
      fontSize: 16,
      color: MD.TEXT,
      outline: "none",
      transition: "box-shadow .15s ease, border-color .15s ease",
    },
    focus: { borderColor: MD.PRIMARY, boxShadow: `0 0 0 4px rgba(0,168,255,0.15)` },
    hint: { marginTop: 6, fontSize: 12, color: MD.MUTED },
    hintError: { color: "#B91C1C" },

    checkRow: { display: "flex", alignItems: "flex-start", gap: 10, marginTop: 16 },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 6,
      border: `1.5px solid ${MD.BORDER}`,
      background: "#FFFFFF",
      appearance: "none",
      outline: "none",
      cursor: "pointer",
      position: "relative",
    },
    checkboxChecked: { borderColor: MD.PRIMARY, background: MD.PRIMARY },
    checkMark: {
      position: "absolute",
      inset: 0,
      maskImage:
        "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"14\" height=\"14\" viewBox=\"0 0 20 20\" fill=\"none\"><path d=\"M5 10.5L8.5 14L15 6\" stroke=\"white\" stroke-width=\"2.4\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>')",
      WebkitMaskImage:
        "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"14\" height=\"14\" viewBox=\"0 0 20 20\" fill=\"none\"><path d=\"M5 10.5L8.5 14L15 6\" stroke=\"white\" stroke-width=\"2.4\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>')",
      backgroundColor: "white",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
    },

    alerts: { marginTop: 8 },
    alertErr: {
      background: MD.ERROR_BG,
      color: MD.ERROR_TEXT,
      border: `1px solid ${MD.ERROR_BORDER}`,
      borderRadius: 12,
      padding: 12,
      fontSize: 14,
    },
    alertOk: {
      background: MD.OK_BG,
      color: MD.OK_TEXT,
      border: `1px solid ${MD.OK_BORDER}`,
      borderRadius: 12,
      padding: 12,
      fontSize: 14,
    },

    actions: { marginTop: 16, display: "flex", gap: 12 },
    submit: {
      padding: "14px 18px",
      borderRadius: 12,
      border: "none",
      fontWeight: 700,
      fontSize: 16,
      color: "#FFFFFF",
      background: MD.PRIMARY,
      cursor: "pointer",
      boxShadow: "0 8px 26px rgba(0,168,255,0.25)",
    },
    submitDisabled: { background: "#A3AEBF", cursor: "not-allowed", boxShadow: "none" },
  };

  const [focusId, setFocusId] = useState("");
  const onFocus = (id) => () => setFocusId(id);
  const onBlur = () => setFocusId("");

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Header */}
        <header style={S.header}>
          <div style={S.brandWrap}>
            {/* Use your deployed MikDok logo asset */}
            <img src="/logo192.png" alt="MikDok" style={S.logo} />
            <div style={S.brandCol}>
              <div style={S.wordmark}>MikDok</div>
              <div style={S.tag}>{t.brandTag}</div>
            </div>
          </div>
          <div style={S.rightHead}>
            <select
              aria-label="UI language"
              value={uiLang}
              onChange={(e) => setUiLang(e.target.value)}
              style={S.langSel}
            >
              <option value="en">English</option>
              <option value="sq">Shqip</option>
            </select>
            <button type="button" onClick={logout} style={S.logout}>
              {t.logout}
            </button>
          </div>
        </header>

        {/* Body */}
        <main style={S.main}>
          <h1 style={S.title}>{t.title}</h1>
          <p style={S.subtitle}>{t.subtitle}</p>

          <form
            style={S.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) save();
            }}
            noValidate
          >
            <label style={S.label} htmlFor="fullName">
              {t.fullName} *
            </label>
            <input
              id="fullName"
              type="text"
              placeholder="Your full name / Emri i plotë"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              onFocus={onFocus("fullName")}
              onBlur={onBlur}
              autoComplete="name"
              style={{ ...S.input, ...(focusId === "fullName" ? S.focus : {}) }}
            />

            <label style={S.label} htmlFor="phone">
              {t.phone} *
            </label>
            <input
              id="phone"
              type="tel"
              placeholder="+355 6X XXX XXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={onFocus("phone")}
              onBlur={onBlur}
              autoComplete="tel"
              inputMode="tel"
              style={{ ...S.input, ...(focusId === "phone" ? S.focus : {}) }}
            />
            <div style={{ ...S.hint, ...(phone && !phoneOk(phone) ? S.hintError : {}) }}>
              {phone && !phoneOk(phone) ? t.phoneInvalid : t.phoneHint}
            </div>

            <label style={S.label} htmlFor="lang">
              {t.lang}
            </label>
            <select
              id="lang"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              onFocus={onFocus("lang")}
              onBlur={onBlur}
              style={{ ...S.select, ...(focusId === "lang" ? S.focus : {}) }}
            >
              <option value="en">English</option>
              <option value="sq">Shqip</option>
              <option value="it">Italiano</option>
              <option value="el">Ελληνικά</option>
            </select>

            <div style={S.checkRow}>
              <div
                role="checkbox"
                aria-checked={privacy}
                tabIndex={0}
                onClick={() => setPrivacy((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    setPrivacy((v) => !v);
                  }
                }}
                style={{ ...S.checkbox, ...(privacy ? S.checkboxChecked : {}) }}
              >
                {privacy && <span style={S.checkMark} />}
              </div>
              <label
                onClick={() => setPrivacy((v) => !v)}
                style={{ fontSize: 13, color: MD.MUTED, cursor: "pointer" }}
              >
                {t.privacy}{" "}
                <a href="/privacy" style={{ color: MD.PRIMARY, textDecoration: "underline" }}>
                  {t.privacyShort}
                </a>{" "}
                *
              </label>
            </div>

            <div style={{ marginTop: 8 }}>
              {err && <div style={S.alertErr}>{err}</div>}
              {ok && <div style={S.alertOk}>{ok}</div>}
            </div>

            <div style={S.actions}>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{ ...S.submit, ...(canSubmit ? {} : S.submitDisabled) }}
              >
                {loading ? "Saving..." : t.save}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
